import { useState, useEffect } from 'react';
import api from '../../../hooks/useApi';
import Modal from '../../shared/Modal';
import { fmtDY } from './planConstants';

/*
  Cost and earned value.

  A percent complete on its own has never been enough to run a job from: a plan
  can be exactly on schedule and quietly losing money, or behind and cheap. The
  three numbers that separate those cases are what this shows.

  Everything measures against the baseline rather than the live plan, because
  measured against the live plan "behind budget" moves every time somebody edits
  a duration, which is the trick that makes a red project look green.
*/

const money = (n) => (n === null || n === undefined
  ? '—'
  : `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

function Index({ label, value, good, hint }) {
  const colour = value === null ? '#9CA3AF' : good(value) ? '#059669' : value > 0.95 ? '#6B7280' : '#DC2626';
  return (
    <div className="flex-1 min-w-[110px] border border-gray-200 rounded px-2 py-1.5" title={hint}>
      <div className="text-[7.5px] font-bold uppercase tracking-wider text-gray-400">{label}</div>
      <div className="text-[16px] font-bold tabular-nums" style={{ color: colour }}>
        {value === null ? '—' : value.toFixed(2)}
      </div>
      <div className="text-[7px] text-gray-400">{hint}</div>
    </div>
  );
}

function Figure({ label, value, hint, tone }) {
  return (
    <div className="flex-1 min-w-[110px] border border-gray-200 rounded px-2 py-1.5">
      <div className="text-[7.5px] font-bold uppercase tracking-wider text-gray-400">{label}</div>
      <div className={`text-[13px] font-bold tabular-nums ${tone === 'bad' ? 'text-red-600' : tone === 'good' ? 'text-emerald-600' : 'text-gray-900'}`}>
        {money(value)}
      </div>
      <div className="text-[7px] text-gray-400">{hint}</div>
    </div>
  );
}

export default function EarnedValuePanel({ sowId, onClose, onOpenTask }) {
  const [ev, setEv] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`sows/${sowId}/earned-value`)
      .then(setEv)
      .catch(e => setError(e.message || 'Could not work out earned value.'));
  }, [sowId]);

  return (
    <Modal title="Cost and earned value" onClose={onClose} wide footer={
      <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>
    }>
      {error && <div className="text-[9px] text-red-600 bg-red-50 px-2 py-1.5 rounded">{error}</div>}
      {!ev && !error && <div className="py-8 text-center text-[10px] text-gray-400">Working it out…</div>}

      {ev && (
        <div className="space-y-3">
          {!ev.has_baseline ? (
            <div className="text-[9px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              <span className="font-semibold">No baseline, so there is nothing to measure against.</span> The plan's
              cost as it stands is {money(ev.planned_cost_now)}. Take a baseline and the schedule and cost indices
              below start to mean something.
            </div>
          ) : (
            <div className="text-[9px] text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
              {ev.verdict}
              <div className="text-[7.5px] text-gray-400 mt-0.5">
                Against {ev.baseline_name}
                {ev.baseline_taken_at ? `, taken ${fmtDY(ev.baseline_taken_at.slice(0, 10))}` : ''}
                , as at {fmtDY(ev.status_date)}.
              </div>
            </div>
          )}

          {/* The two indices, which are the headline */}
          <div className="flex gap-2 flex-wrap">
            <Index label="SPI — schedule" value={ev.spi} good={v => v >= 1}
                   hint="Work earned versus work due. Below 1 is behind." />
            <Index label="CPI — cost" value={ev.cpi} good={v => v >= 1}
                   hint="Work earned per £1 spent. Below 1 is over cost." />
            <div className="flex-1 min-w-[110px] border border-gray-200 rounded px-2 py-1.5">
              <div className="text-[7.5px] font-bold uppercase tracking-wider text-gray-400">Progress</div>
              <div className="text-[16px] font-bold tabular-nums text-gray-900">{ev.percent_complete}%</div>
              <div className="text-[7px] text-gray-400">by value, against {ev.percent_spent}% of the budget spent</div>
            </div>
          </div>

          {/* The three raw numbers underneath, so nothing is a black box */}
          <div className="flex gap-2 flex-wrap">
            <Figure label="Planned to date" value={ev.bcws} hint="What the baseline said we would have spent by now" />
            <Figure label="Earned" value={ev.bcwp} hint="Baseline cost of the work actually done"
                    tone={ev.schedule_variance < 0 ? 'bad' : undefined} />
            <Figure label="Actual" value={ev.acwp} hint="What it has really cost"
                    tone={ev.cost_variance < 0 ? 'bad' : undefined} />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Figure label="Budget (BAC)" value={ev.bac} hint="The baselined cost of the whole plan" />
            <Figure label="Forecast out-turn (EAC)" value={ev.eac}
                    hint="Budget at the cost performance so far"
                    tone={ev.vac !== null && ev.vac < 0 ? 'bad' : ev.vac > 0 ? 'good' : undefined} />
            <Figure label="Left to spend (ETC)" value={ev.etc} hint="Forecast out-turn less what has gone" />
            {ev.sow_value != null && (
              <Figure label="SOW value" value={ev.sow_value} hint="What the client is paying, excluding VAT" />
            )}
          </div>

          {(ev.uncosted_scope > 0 || ev.unpriced_tasks > 0) && (
            <div className="text-[8.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 space-y-0.5">
              {ev.uncosted_scope > 0 && (
                <div>
                  The plan now costs <span className="font-semibold">{money(ev.uncosted_scope)}</span> more than the
                  baseline. That is scope added since it was taken, and it is usually the real story behind a plan
                  that is somehow both on schedule and over budget. Consider re-baselining it under a change note.
                </div>
              )}
              {ev.unpriced_tasks > 0 && (
                <div>
                  <span className="font-semibold">{ev.unpriced_tasks} task{ev.unpriced_tasks !== 1 ? 's have' : ' has'} no cost</span>,
                  because nobody internal is assigned or their band has no rate on this SOW's rate card. Those are
                  missing from every figure above.
                </div>
              )}
            </div>
          )}

          {/* Where the money is going wrong, worst first */}
          {ev.tasks.length > 0 && (
            <div>
              <div className="text-[8px] font-bold text-gray-500 uppercase mb-1">By task, worst variance first</div>
              <div className="overflow-x-auto max-h-[38vh] overflow-y-auto border border-gray-200 rounded">
                <table className="w-full">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left text-[7.5px] uppercase text-gray-400 font-bold">Task</th>
                      <th className="px-2 py-1 text-right text-[7.5px] uppercase text-gray-400 font-bold">Budget</th>
                      <th className="px-2 py-1 text-right text-[7.5px] uppercase text-gray-400 font-bold">Earned</th>
                      <th className="px-2 py-1 text-right text-[7.5px] uppercase text-gray-400 font-bold">Actual</th>
                      <th className="px-2 py-1 text-right text-[7.5px] uppercase text-gray-400 font-bold">Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ev.tasks.map(t => (
                      <tr key={t.task_id} className="border-t border-gray-50 hover:bg-gray-50/60">
                        <td className="px-2 py-1 text-[8.5px] text-gray-700">
                          <button onClick={() => onOpenTask?.(t.task_id)} className="text-left hover:text-gold">
                            <span className="font-mono text-[7px] text-gray-300 mr-1">{t.wbs_code}</span>
                            {t.name}
                          </button>
                          <span className="text-[7px] text-gray-400 ml-1">{t.completion_pct}%</span>
                        </td>
                        <td className="px-2 py-1 text-[8.5px] text-right tabular-nums text-gray-500">{money(t.budget)}</td>
                        <td className="px-2 py-1 text-[8.5px] text-right tabular-nums text-gray-700">{money(t.earned)}</td>
                        <td className="px-2 py-1 text-[8.5px] text-right tabular-nums text-gray-700">{money(t.actual_cost)}</td>
                        <td className={`px-2 py-1 text-[8.5px] text-right tabular-nums font-semibold ${t.cost_variance < 0 ? 'text-red-600' : t.cost_variance > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                          {t.cost_variance === 0 ? '—' : money(t.cost_variance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="text-[7.5px] text-gray-400">
            Planned cost is each assignee's days at their band rate on this SOW's rate card. Actual cost comes from
            the rate recorded on each time entry, so it is what was charged at the time rather than what the card
            says today.
          </div>
        </div>
      )}
    </Modal>
  );
}
